<template>
  <div class="flex flex-wrap gap-4 mt-6 mb-12">
    <div class="bg-white w-[99%] m-auto shadow-ts1 rounded-lg pb-3">
      <div class="border-b border-gray-300 w-full flex justify-between items-center px-4 md:px-8 pt-5 pb-4">
        <h2 class="font-display text-xl font-bold text-blue">{{ $t('payment_history').tTitle() }}</h2>
        <button @click="exportCSVData" class="appearance-none py-1 text-base rounded-full text-white bg-blue w-24 text-center font-bold">
          {{ $t('export').tTitle() }}
        </button>
      </div>
      <div v-if="payments" class="p-2 md:p-6 justify-center">
        <div class="hidden lg:block overflow-x-auto overflow-y-visible pb-6" id="paymentTable">
          <table class="min-w-[1100px] w-full border-collapse">
            <thead class="font-bold ltr:text-left rtl:text-right text-blue">
              <tr class="h-[50px]">
                <th class="ltr:pl-8 rtl:pr-8">{{ $t('id') }}</th>
                <th>{{ $t('date').tTitle() }}</th>
                <th>{{ $t('account').tTitle() }}</th>
                <th>{{ $t('type').tTitle() }}</th>
                <th>{{ $t('gateway').tTitle() }}</th>
                <th>{{ $t('currency').tTitle() }}</th>
                <th>{{ $t('amount').tTitle() }}</th>
                <th>{{ $t('status').tTitle() }}</th>
                <th>{{ $t('details').tTitle() }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(payment, i) in payments" :key="'payments-' + i" class="odd:bg-gray-100 even:bg-white py-4 h-[50px]">
                <td class="font-bold ltr:rounded-l-xl rtl:rounded-r-xl ltr:pl-8 rtl:pr-8">#{{ payment.id }}</td>
                <td>{{ $formatDateTime(payment.date) }}</td>
                <td>{{ getTradingAccountTitle(payment.trading_account) }}</td>
                <td>
                  <!-- Case: Transfer -->
                  <span v-if="payment.type.includes('transfer')">
                    <span v-if="payment.type.includes('withdrawal')">{{ $t('transfer_out').tTitle() }}</span>
                    <span v-else-if="payment.type.includes('deposit')">{{ $t('transfer_in').tTitle() }}</span>
                  </span>
                  <!-- Case: Local Depositor -->
                  <span v-else-if="payment.type.includes('local depositor')">
                    <span v-if="payment.type.includes('withdrawal')">{{ $t('transfer_out').tTitle() }} ({{ $t('local_depositor') }})</span>
                    <span v-else-if="payment.type.includes('deposit')">{{ $t('transfer_in').tTitle() }} ({{ $t('local_depositor') }})</span>
                  </span>
                  <!-- Other Case -->
                  <span v-else>{{ $t(payment.type).tTitle() }}</span>
                </td>
                <td>
                  <div class="flex items-center gap-3 relative" v-closable="{ handler: 'closeTooltip', isOpen: hoveredTooltip === payment.id }">
                    {{ renderGatewayContent(payment) }}
                    <div class="cursor-pointer" v-if="payment.type === 'withdrawal' && renderGatewayContent(payment).length > 1 && payment.thirdparty_ipn_response && ['pending', 'requested'].includes(payment.status)" @click.stop="handleGatewayInfoClick(payment, $event)">
                      <img class="w-[13px] h-[13px]" src="../../assets/img/icons/upload/info.svg" alt="Additional info" />
                    </div>
                    <div
                      v-if="hoveredTooltip === payment.id"
                      class="fixed z-[9999] min-w-[215px] border border-gray-300 bg-white rounded-lg shadow-ts3"
                      :style="{ top: tooltipPosition.top + 'px', left: tooltipPosition.left + 'px' }"
                    >
                      <div class="font-body text-base leading-7 text-gray-900 text-left font-normal m-auto lg:m-0 lg:float-right w-full p-5 object-right-bottom">
                        <p class="text-nowrap">{{ $t('coin').tTitle() }}: {{ payment.coin }}</p>
                        <p class="text-nowrap">{{ $t('network').tTitle() }}: {{ payment.network }}</p>
                        <p class="text-nowrap">{{ $t('wallet_address').tTitle() }}: {{ payment.wallet_address }}</p>
                      </div>
                    </div>
                  </div>
                </td>
                <td>{{ chooseDisplayCurrency(payment) }}</td>
                <td :class="getValueColor(payment.type)">{{ chooseDisplayValue(payment) }}</td>
                <td>
                  <span v-if="payment.status === 'processed'">
                    <span class="icon has-text-success">
                      <i class="fa fa-check"></i>
                    </span>
                  </span>
                  <span v-if="payment.status === 'cancelled'">
                    <span class="icon">
                      <i class="fa fa-times"></i>
                    </span>
                  </span>
                  <span v-if="payment.status === 'pending'">
                    <span class="icon">
                      <i class="fa fa-clock-o"></i>
                    </span>
                  </span>
                  {{ $t(payment.status) }}
                </td>
                <td class="ltr:rounded-r-xl rtl:rounded-l-xl text-center w-32">
                  <div class="flex justify-start items-center">
                    <div v-if="payment.type === 'withdrawal' && payment.thirdparty_ipn_response && !['pending', 'requested'].includes(payment.status)" class="w-24 flex justify-center">
                      <div
                        @click="showTransactionDetails(payment)"
                        class="tooltip-icon h-7 w-7 border border-gray-600 text-gray-600 text-[27px] ltr:pl-[1px] rtl:pl-[1px] pb-[16px] border-solid bg-white rounded-full flex justify-center items-center cursor-pointer p-auto"
                      >
                        ...
                      </div>
                    </div>
                    <div v-if="payment.gateway == 'Bank Transfer' && payment.status=='approved' && payment.type == 'deposit'" class="w-24 flex justify-center">
                      <div
                        @click="showTransactionDetails(payment)"
                        class="tooltip-icon h-7 w-7 border border-gray-600 text-gray-600 text-[27px] ltr:pl-[1px] rtl:pl-[1px] pb-[16px] border-solid bg-white rounded-full flex justify-center items-center cursor-pointer p-auto"
                      >
                        ...
                      </div>
                    </div>
                    <Button @click="cancelPendingWithdrawal(payment.id)" mode="dark" customClasses="!w-24 " v-if="payment.type === 'withdrawal' && ['pending', 'requested'].includes(payment.status) && payment.thirdparty_ipn_response && payment.thirdparty_status == ''" size="small">
                      {{ $t('cancel').tTitle() }}
                    </Button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="w-full lg:hidden text-body p-2">
          <div
            v-for="(payment, i) in payments"
            :key="'payment-' + i"
            :id="'payment-' + i"
            class="flex justify-between px-4 pt-[13px] rounded-lg odd:bg-gray-400 relative"
            :class="{
              'max-h-[50px]': !payment.expanded,
              'overflow-hidden': hoveredTooltip !== payment.id,
              'overflow-visible': hoveredTooltip === payment.id,
            }"
          >
            <div class="text-blue font-bold">
              <label class="block h-9">
                <img
                  @click="expandPayment(i)"
                  width="12px"
                  height="12px"
                  class="ltr:mr-1 rtl:ml-1 cursor-pointer inline"
                  :class="{ 'ltr:-rotate-90 rtl:rotate-90': !payment.expanded }"
                  src="../../assets/img/platform-dropdown.svg"
                />
                {{ $t('id').tTitle() }}
              </label>
              <label class="block h-9">{{ $t('date').tTitle() }}</label>
              <label class="block h-9">{{ $t('account').tTitle() }}</label>
              <label class="block h-9">{{ $t('type').tTitle() }}</label>
              <label class="block h-9">{{ $t('gateway').tTitle() }}</label>
              <label class="block h-9">{{ $t('currency').tTitle() }}</label>
              <label class="block h-9">{{ $t('amount').tTitle() }}</label>
              <label class="block h-9">{{ $t('status').tTitle() }}</label>
              <label class="block h-9">{{ $t('details').tTitle() }}</label>
            </div>
            <div class="ltr:text-right rtl:text-left">
              <p class="h-9">#{{ payment.id }}</p>
              <p class="h-9">{{ $formatDateTime(payment.date) }}</p>
              <p class="h-9">{{ getTradingAccountTitle(payment.trading_account) }}</p>
              <p class="h-9">
                <!-- Case: Transfer -->
                <span v-if="payment.type.includes('transfer')">
                  <span v-if="payment.type.includes('withdrawal')">
                    {{ $t('transfer_out').tTitle() }}
                  </span>
                  <span v-else-if="payment.type.includes('deposit')">
                    {{ $t('transfer_in').tTitle() }}
                  </span>
                </span>
                <!-- Case: Local Depositor -->
                <span v-else-if="payment.type.includes('local depositor')">
                  <span v-if="payment.type.includes('withdrawal')"> {{ $t('transfer_out').tTitle() }} ({{ $t('local_depositor') }}) </span>
                  <span v-else-if="payment.type.includes('deposit')"> {{ $t('transfer_in').tTitle() }} ({{ $t('local_depositor') }}) </span>
                </span>
                <!-- Other Case -->
                <span v-else>{{ payment.type.tTitle() }}</span>
              </p>
              <div v-closable="{ handler: 'closeTooltip', isOpen: hoveredTooltip === payment.id }" class="h-9 flex w-full items-center justify-end gap-1">
                <p>{{ renderGatewayContent(payment) }}</p>
                <div v-if="payment.type === 'withdrawal' && renderGatewayContent(payment).length > 1 && payment.thirdparty_ipn_response && ['pending', 'requested'].includes(payment.status)" @click="handleGatewayInfoClick(payment)">
                  <img class="w-[13px] h-[13px] cursor-pointer" src="../../assets/img/icons/upload/info.svg" alt="Additional info" />
                </div>

                <div v-if="hoveredTooltip == payment.id" class="absolute w-[calc(100%)] left-0 top-48 z-[1] bg-white border border-gray-300 rounded-lg shadow-ts3">
                  <div class="font-body text-base leading-7 text-gray-900 text-left font-normal m-auto lg:m-0 lg:float-right w-full max-w-[348px] p-5 object-right-bottom">
                    <p>{{ $t('coin').tTitle() }}: {{ payment.coin }}</p>
                    <p class="break-all">{{ $t('network').tTitle() }}: {{ payment.network }}</p>
                    <p class="break-all">{{ $t('wallet_address').tTitle() }}: {{ payment.wallet_address }}</p>
                  </div>
                </div>
              </div>

              <p class="h-9">{{ chooseDisplayCurrency(payment) }}</p>
              <p class="h-9" :class="getValueColor(payment.type)">{{ chooseDisplayValue(payment) }}</p>
              <p class="h-9">
                <span v-if="payment.status === 'processed'">
                  <span class="icon has-text-success">
                    <i class="fa fa-check"></i>
                  </span>
                </span>
                <span v-if="payment.status === 'cancelled'">
                  <span class="icon">
                    <i class="fa fa-times"></i>
                  </span>
                </span>
                <span v-if="payment.status === 'pending'">
                  <span class="icon">
                    <i class="fa fa-clock-o"></i>
                  </span>
                </span>
                {{ $t(payment.status) }}
              </p>
              <div class="h-9 flex justify-end" :class="{ 'mb-2': payment.type === 'withdrawal' && ['pending', 'requested'].includes(payment.status) }">
                <span
                  @click="showTransactionDetails(payment)"
                  v-if="payment.type === 'withdrawal' && payment.thirdparty_ipn_response && !['pending', 'requested'].includes(payment.status)"
                  class="h-7 w-7 border border-gray-600 text-gray-600 text-[27px] ltr:pl-[1px] rtl:pl-[1px] pb-[16px] border-solid bg-white rounded-full flex justify-center items-center cursor-pointer p-auto"
                >
                  ...
                </span>
                <span
                  @click="showTransactionDetails(payment)"
                  v-if="payment.gateway == 'Bank Transfer' && payment.status=='approved' && payment.type == 'deposit'"
                  class="h-7 w-7 border border-gray-600 text-gray-600 text-[27px] ltr:pl-[1px] rtl:pl-[1px] pb-[16px] border-solid bg-white rounded-full flex justify-center items-center cursor-pointer p-auto"
                >
                  ...
                </span>
                <Button @click="cancelPendingWithdrawal(payment.id)" mode="dark" customClasses="!w-24 h-7" v-if="payment.type === 'withdrawal' && ['pending', 'requested'].includes(payment.status) && payment.thirdparty_ipn_response && payment.thirdparty_status == ''" size="small">
                  {{ $t('cancel').tTitle() }}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-center content-center w-full py-5 mx-8 relative">
          <div class="w-[100px] h-[40px] font-body text-sm leading-10 absolute ltr:left-0 rtl:right-0">{{ this.pagination.total }} {{ $t('records') }}</div>
          <div v-if="this.pagination.total > pagination.rowsPerPage" class="flex h-[40px] font-body text-center leading-10">
            <div class="text-center">
              <pagination-component :page="pagination.page" :maxPerPage="pagination.rowsPerPage" :total="pagination.total" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="my-10">
        <p class="text-center text-base text-blue font-semibold">{{ $t('no_records') }}</p>
      </div>
    </div>
    <!-- <pagination
      for="payment-history"
      :records="pagination.total"
      :per-page="pagination.maxItemsPerPage"
      :chunk="4"
      :count-text="pagination.total + ' ' + $t('records')"
    ></pagination> -->

    <!-- <div class="bg-white w-full shadow-ts1 rounded-lg">
      <div class="border-b border-gray-300 w-full px-4 md:px-8 pt-5 pb-4">
        <h2 class="font-display text-xl font-bold text-blue leading-none">{{$t('my_cards').tTitle()}}</h2>
      </div>
      <div class="p-6 justify-center">
        <my-cards />
      </div>
    </div> -->
    <modal
      :opened="transactionDetailsModal.detailsModal"
      :title="$t('details').tTitle()"
      @close="resetTransactionDetails()"
      customClasses="w-[92vw] sm:w-[520px] md:w-[640px] max-h-[82vh] px-6 sm:px-[30px] py-10"
    >
      <div v-if="transactionDetailsModal.TXID">
        <p class="text-gray-900 text-base leading-8 ltr:text-left rtl:text-right mb-1">
          <span v-if="transactionDetailsModal.gateway=='Confirmo'">{{ $t('txid') }}</span>
          <span v-else>{{ $t('transaction_id') }}</span>
        </p>

        <div class="relative bg-gray-100 rounded-md ltr:text-left rtl:text-right h-[42px] flex items-center">
          <p class="text-gray-900 text-xs font-display font-bold ltr:pl-3 ltr:pr-12 rtl:pr-3 rtl:pl-12 truncate" :title="transactionDetailsModal.TXID">
            {{ transactionDetailsModal.TXID }}
          </p>

          <div @click="copyTXID()" class="absolute ltr:right-2 rtl:left-2 top-2 cursor-pointer">
            <img src="../../assets/img/copy-icon.svg" width="28" />
          </div>
        </div>
      </div>

      <div v-if="transactionDetailsModal.address">
        <p class="text-gray-900 text-base leading-8 ltr:text-left rtl:text-right mb-1 mt-4">
          {{ $t('wallet_address').tTitle() }}
        </p>

        <div class="relative bg-gray-100 rounded-md ltr:text-left rtl:text-right h-[42px] flex items-center">
          <p class="text-gray-900 text-xs font-display font-bold ltr:pl-3 ltr:pr-12 rtl:pr-3 rtl:pl-12 truncate" :title="transactionDetailsModal.address">
            {{ transactionDetailsModal.address }}
          </p>

          <div @click="copyAddress()" class="absolute ltr:right-2 rtl:left-2 top-2 cursor-pointer">
            <img src="../../assets/img/copy-icon.svg" width="28" />
          </div>
        </div>
      </div>

      <div v-if="transactionDetailsModal.gateway == 'Bank Transfer' && transactionDetailsModal.type=='deposit'">
        <p class="text-gray-900 text-base leading-8 ltr:text-left rtl:text-right mb-1 mt-4">
          {{ $t('request_amount').tTitle() }}
        </p>

        <div class="relative bg-gray-100 rounded-md ltr:text-left rtl:text-right h-[42px] flex items-center">
          <p class="text-gray-900 text-xs font-display font-bold ltr:pl-3 ltr:pr-12 rtl:pr-3 rtl:pl-12 truncate">
            {{ $formatMoney(transactionDetailsModal.requestedValue) }}
          </p>
        </div>
      </div>

    </modal>
  </div>
</template>

<script>
import { service } from '@/main.js'
import { mapGetters, mapActions } from 'vuex'
import config from '../../config'
import { exportCSVFile } from '@/utils/csvUtils'

// import MyCards from './MyCards.vue'
import PaginationComponent from '@/components/Pagination.vue'
import Modal from '@/components/reusableComponents/Modal.vue'
import Button from '@/components/reusableComponents/Button.vue'

export default {
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__clickOutsideBinding__ = binding.value
        el.__clickOutside__ = event => {
          const { handler, isActive } = el.__clickOutsideBinding__ || {}

          if (!isActive || el.contains(event.target)) {
            return
          }

          handler(event)
        }

        document.addEventListener('click', el.__clickOutside__)
        document.addEventListener('touchstart', el.__clickOutside__)
      },
      update(el, binding) {
        el.__clickOutsideBinding__ = binding.value
      },
      unbind(el) {
        document.removeEventListener('click', el.__clickOutside__)
        document.removeEventListener('touchstart', el.__clickOutside__)
        delete el.__clickOutside__
        delete el.__clickOutsideBinding__
      },
    },
  },
  components: {
    // MyCards,
    PaginationComponent,
    Modal,
    Button,
  },
  data() {
    return {
      payments: [],
      currencySymbols: config.currencies,
      pagination: {
        page: 1,
        rowsPerPage: 10,
        total: 0,
      },
      transactionDetailsModal: {
        detailsModal: false,
        TXID: '',
        address: '',
        requestedValue: '',
        gateway: '',
        type: ''
      },
      showTooltip: false,
      hoveredTooltip: null,
      tooltipPosition: {
        top: 0,
        left: 0,
      },
    }
  },
  watch: {
    'pagination.page'() {
      this.getPayments()
    },
  },
  computed: {
    ...mapGetters({
      User: 'user/all',
      Language: 'language/code',
      logoLoaderActive: 'logoLoader/active',
    }),
  },
  mounted() {
    this.getPayments()
    this.getTradingAccountTitle()
    //hint if dragger of the container doesn't work look for the data to not be null ,
    // If the DOM element with the specified id
    // is not available at the time dragContainer is called,
    this.dragContainer('#paymentTable')
    document.addEventListener('scroll', this.closeTooltip, true)
  },
  beforeDestroy() {
    document.removeEventListener('scroll', this.closeTooltip, true)
  },
  created() {
    this.$bus.$on('pageChanged', p => {
      this.pagination.page = p
    })
  },
  methods: {
    getPayments() {
      // this['logoLoader/set'](true)

      service
        .post(`/finance/client/${this.User.info.ID}/history`, {
          userID: this.User.info.ID,
          page: this.pagination.page,
          'include-external': true,
        })
        .then(response => {
          this.payments = response.data.payments
          this.pagination.total = response.data.count

          for (let p in this.payments) {
            this.$set(this.payments[p], 'expanded', false)
          }
          // this['logoLoader/set'](false)
        })
        .catch(() => {
          // this['logoLoader/set'](false)
        })
    },
    chooseDisplayValue(payment) {
      if (payment.value == 0 || ((parseFloat(payment.value) + parseFloat(payment.fee)).toFixed(2) == 10.01 && parseFloat(payment.value_requested) == 10)) {
        return this.$formatMoney(payment.value_requested, this.currencySymbols[payment.currency])
      } else if (
        payment.gateway &&
        (payment.gateway.indexOf('Crypto') !== -1 || payment.gateway.indexOf('Confirmo') !== -1) &&
        payment.type &&
        payment.type.indexOf('withdrawal') !== -1
      ) {
        let paymentCurrency = payment.currency
        if (this.User && this.User.wallet && this.User.wallet.currency) {
          paymentCurrency = this.User.wallet.currency
        }
        return this.$formatMoney(payment.value_requested, this.currencySymbols[paymentCurrency])
      } else {
        return this.$formatMoney(parseFloat(payment.value), this.currencySymbols[payment.currency])
      }
    },
    chooseDisplayCurrency(payment) {
      if (
        payment.gateway &&
        (payment.gateway.indexOf('Crypto') !== -1 || payment.gateway.indexOf('Confirmo') !== -1) &&
        payment.type &&
        payment.type.indexOf('withdrawal') !== -1 &&
        this.User &&
        this.User.wallet &&
        this.User.wallet.currency
      ) {
        return this.User.wallet.currency
      } else {
        return payment.currency
      }
    },
    getValueColor(paymentType) {
      const lower = paymentType.toLowerCase()
      if (/\bdeposit\b/.test(lower)) {
        return 'text-green'
      } else {
        return 'text-red'
      }
    },
    expandPayment(i) {
      this.$set(this.payments, i, { ...this.payments[i], expanded: !this.payments[i].expanded })
    },
    /**
     * Function to return the content of gateway column on payment history table depending on different cases.
     */
    renderGatewayContent(payment) {
      if (payment?.extra_info?.toLowerCase().includes('ib commission')) {
        return 'IB Commissions'
      } else if (payment.gateway == 'VLoad') {
        return 'PayRedeem'
      } else if (payment.gateway == '') {
        return '-'
      } else {
        return payment.gateway
      }
    },
    getTradingAccountTitle(account) {
      if (account === '-1') {
        return this.$t('wallet').tTitle()
      } else if (isNaN(account)) {
        return this.$t(account).tTitle()
      } else {
        return account
      }
    },
    /**
     * Function to export payment history into a csv file
     */
    async exportCSVData() {
      let CsvData
      let IBClientsArgs = {
        userID: this.User.info.ID,
        'include-external': true,
        page: 1,
        export: true,
      }
      try {
        const response = await service.post(`/finance/client/${this.User.info.ID}/history`, IBClientsArgs)
        if (response && response.data && response.data.payments) {
          CsvData = response.data.payments
        }
      } catch (error) {
        return
      }

      const headers = {
        id: 'ID',
        date: 'Date',
        trading_account: 'Account',
        type: 'Type',
        gateway: 'Gateway',
        currency: 'Currency',
        value: 'Amount',
        status: 'Status',
        txid: 'TXID',
        address: 'Address',
        requested_amount: 'Requested Amount'
      }

      const itemsFormatted = CsvData.map(item => ({
        id: item.id,
        date: item.date,
        trading_account: item.trading_account,
        type: item.type,
        gateway: item.gateway,
        currency: item.currency,
        value: item.value,
        status: item.status,
        txid: item.thirdparty_ipn_response ? JSON.parse(item.thirdparty_ipn_response).TXID : item.gateway == 'Bank Transfer' && item.extra_info ? JSON.parse(item.extra_info).transaction_id : '',
        address: item.thirdparty_ipn_response ? JSON.parse(item.thirdparty_ipn_response).Address : '',
        requested_amount: item.gateway == 'Bank Transfer' ? item.value_requested: ''
      }))

      const fileTitle = 'Payment History'
      exportCSVFile(headers, itemsFormatted, fileTitle)
    },

    copyText(text) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text)
        } else {
          this.fallbackCopy(text)
        }
      } catch (e) {
        this.fallbackCopy(text)
      }

      // Toast
      this.$toasted.success(this.$t('copied'))
    },
    /**  * Fallback method for copying text to clipboard for older browsers
     */
    fallbackCopy(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.top = '-9999px'
      textarea.style.left = '-9999px'

      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    },
    /**  * Function to copy TXID to clipboard
     */
    copyTXID() {
      this.copyText(this.transactionDetailsModal.TXID)
    },
    /**  * Function to copy Address to clipboard
     */
    copyAddress() {
      this.copyText(this.transactionDetailsModal.address)
    },
    /** * Function to show transaction details modal
     */
    showTransactionDetails(payment) {
      this.transactionDetailsModal.detailsModal = true
      this.transactionDetailsModal.gateway = payment.gateway
      this.transactionDetailsModal.type = payment.type
      if(payment.gateway == 'Confirmo'){
        let paymentDetails = JSON.parse(payment.thirdparty_ipn_response)
        this.transactionDetailsModal.TXID = paymentDetails.TXID
        this.transactionDetailsModal.address = paymentDetails.Address
      }
      else if(payment.gateway == 'Bank Transfer'){
        // const extraInfo = JSON.parse(payment.extra_info);
        // this.transasctionDetailsModal.TXID = extraInfo.transaction_id
        if(payment.type == 'deposit'){
          this.transactionDetailsModal.requestedValue = payment.value_requested
        }
      }
    },
    /** * Function to reset transaction details modal data
     */
    resetTransactionDetails() {
      Object.assign(this.transactionDetailsModal, {
        detailsModal: false,
        TXID: '',
        address: '',
        requestedValue: '',
        gateway: '',
        type: ''
      })
    },
    /** * Function to reset transaction details modal data
     */
    async cancelPendingWithdrawal(id) {
      try {
        const response = await service.post('finance/cancel-client-pending-withdrawal', {
          paymentID: parseInt(id),
        })
        if (response.data && response.data.success === true) {
          this.$toasted.success(this.$t('cancel_transaction_success'))
          this.getPayments()
          return
        }
      } catch (error) {
        if (typeof error.response?.data !== 'undefined') {
          const messageId = error.response.data?.meta?.message_id
          this.$toasted.error(this.$t(messageId), { duration: 10000 })
          if (messageId === 'withdrawal_already_processed') {
            this.getPayments()
          }
          return
        }
      }
      this.$toasted.error(this.$t('error_contact_support'))
    },
    closeTooltip() {
      this.hoveredTooltip = null
    },
    handleGatewayInfoClick(payment, event) {
      if (this.hoveredTooltip === payment.id) {
        this.hoveredTooltip = null
      } else {
        this.hoveredTooltip = payment.id
        if (event && event.currentTarget) {
          const rect = event.currentTarget.getBoundingClientRect()
          this.tooltipPosition = {
            top: rect.bottom + 8,
            left: rect.left,
          }
        }
      }
      let paymentDetails = JSON.parse(payment.thirdparty_ipn_response)
      payment.coin = paymentDetails.Coin
      payment.network = paymentDetails.Network
      payment.wallet_address = paymentDetails.WalletAddress
    },
    ...mapActions(['logoLoader/set', 'user/setInfo']),
  },
}
</script>
<style scoped>
#paymentTable::-webkit-scrollbar {
  height: 4px;
  width: 4px;
}

#paymentTable::-webkit-scrollbar-thumb {
  background: #2b63e3;
  border-radius: 12px;
}
#paymentTable::-webkit-scrollbar-track {
  background: #e9edf4;
  border-radius: 12px;
}
#paymentTable::-webkit-scrollbar {
  height: 4px;
  width: 4px;
}
/* Track */
#paymentTable::-webkit-scrollbar-track {
  background: #e9edf4;
  border-radius: 12px;
}
/* Handle */
#paymentTable::-webkit-scrollbar-thumb {
  background: #2b63e3;
  border-radius: 12px;
}

#paymentTable {
  scroll-behavior: smooth;
}

#paymentTable.active {
  scroll-behavior: auto;
}
</style>
